import { filter, BehaviorSubject, forkJoin, Observable, catchError, of, skip } from 'rxjs';

/**
 * @module ShareService
 */
function createShareService(apiService, deviceService, logService, util, _window) {
    const _shareService = {};

    const sharesSubject = new BehaviorSubject();
    let loadedData = false;

    function log(params) {
        params.module = 'shareService';
        logService.log(params);
        return params;
    }

    function clear() {
        sharesSubject.next();
        loadedData = false;
    }

    function shareSort(a, b) {
        if (a.deviceId < b.deviceId) {
            return -1;
        }
        if (a.deviceId > b.deviceId) {
            return 1;
        }
        if (a.username < b.username) {
            return -1;
        }
        if (a.username > b.username) {
            return 1;
        }
        return 0;
    }

    function inviteToShare(invite) {
        return {
            userId: undefined,
            username: invite.emailAddress || invite.smsNumber,
            deviceId: invite.deviceId,
            roleIdList: Array.isArray(invite?.roleIdList)
                ? invite.roleIdList
                : undefined,
            inviteId: invite.id,
            expiration: (invite?.expirationDttm) ? new _window.Date(invite.expirationDttm) : undefined,
            note: invite.note,
            isFriend: undefined,
            inviterId: invite.inviterId,
            inviterUsername: invite.inviterUsername,
            isOwner: false,
            isGrant: false
        };
    }

    function calcSharesFromGrants(grants, devices, shares, traceId) {
        if (!Array.isArray(grants)) {
            log({
                code: 'noGrantData',
                error: (grants?.error),
                traceId: traceId
            });
            return;
        }
        if (!Array.isArray(devices)) {
            log({
                code: 'noDeviceDataGrants',
                error: devices?.error,
                traceId: traceId
            });
            return;
        }

        grants.forEach(grant => {
            if (grant.status === 'Active' || grant.status === 'A') {

                // find existing share if there is any
                const share = shares.find(share => share.deviceId === grant.deviceId && share.userId === grant.userId);

                if (share && share.roleIdList.indexOf(grant.roleId) === -1) {
                    share.roleIdList.push(grant.roleId);
                } else {
                    // determine if the grant user owns the grant device
                    let isOwner = false;

                    const device = devices.find(d => d.id === grant.deviceId);
                    if (device && device.ownership && grant.userId === device.ownership.ownerId) {
                        isOwner = true;
                    }

                    shares.push({
                        id: grant.id,
                        userId: grant.userId,
                        username: grant.username,
                        deviceId: grant.deviceId,
                        roleIdList: [grant.roleId],
                        inviteId: undefined,
                        expiration: undefined,
                        isFriend: undefined,
                        note: undefined,
                        isOwner: isOwner,
                        isGrant: true
                    });
                }
            }
        });
    }

    function calcSharesFromFriends(friends, devices, shares, traceId) {
        if (!Array.isArray(friends)) {
            log({
                code: 'noFriendData',
                error: friends?.error,
                traceId: traceId
            });
            return;
        }
        if (!Array.isArray(devices)) {
            log({
                code: 'noDeviceDataFriends',
                error: devices?.error,
                traceId: traceId
            });
            return;
        }

        friends.forEach(friend => {
            devices.forEach(device => {
                if (!device.status.deleted && device.permissions && device.permissions.accessControl) {
                    // find a share that matches the friend and device
                    const share = shares.find(share => share.deviceId === device.id && share.userId === friend.userId);
                    if (share) {
                        share.isFriend = true;
                    } else {
                        // create a share without any access
                        shares.push({
                            userId: friend.userId,
                            username: friend.username,
                            deviceId: device.id,
                            roleIdList: [],
                            inviteId: undefined,
                            expiration: undefined,
                            isFriend: true,
                            note: undefined,
                            isOwner: false,
                            isGrant: false
                        });
                    }
                }
            });
        });
    }

    function calcSharesFromInvites(invites, shares, traceId) {
        if (Array.isArray(invites)) {
            invites.forEach(invite => {
                shares.push(inviteToShare(invite));
            });
        } else {
            log({
                code: 'noInviteData',
                error: invites?.error,
                traceId: traceId
            });
        }
    }

    /**
     * @param {Array<Grant>} data.grants
     * @param {Array<Friend>} data.friends
     * @param {Array<Invite>} data.invites
     * @param {Array<Device>} data.devices
     * @param {String} data.traceId
     * @returns {Array<Share>} Array of shares
     */
    function calcShares(data) {
        const shares = [];
        calcSharesFromGrants(data.grants, data.devices, shares, data.traceId);
        calcSharesFromFriends(data.friends, data.devices, shares, data.traceId);
        calcSharesFromInvites(data.invites, shares, data.traceId);
        shares.sort(shareSort);
        return shares;
    }

    function getGrants(params) {
        return new Observable(subscriber => {
            apiService.devices.getDirectAccessGrants(params).then(
                grantsResult => {
                    subscriber.next(grantsResult.data.items);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'getGrantsFail',
                        error: e,
                        traceId: params?.traceId
                    })
                )
            );
        });
    }

    function getFriends(params) {
        return new Observable(subscriber => {
            apiService.friends.get(params).then(
                friendsResult => {
                    subscriber.next(friendsResult.data.items);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'getFriendsFail',
                        error: e,
                        traceId: params?.traceId
                    })
                )
            );
        });
    }

    function getInvites(params) {
        return new Observable(subscriber => {
            apiService.devices.getInvites(params).then(
                invitesResult => {
                    subscriber.next(invitesResult.data.items);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'getInvitesFail',
                        error: e,
                        traceId: params?.traceId
                    })
                )
            );
        });
    }

    function refreshShares(subscriber, result) {
        // The first emitted value will always be synchronous,
        // and it needs to wait for the refresh, so skip the first value.
        _shareService.get({
            refresh: true
        }).pipe(skip(1)).subscribe({
            next() {
                subscriber.next(result);
                subscriber.complete();
            },
            error(e) {
                subscriber.error(e);
            }
        });
    }

    // ****************************
    // public members
    // ****************************

    /**
     * @description gets share data, which is compraised of grants, friends and invites -
     *     all combined to a unified list suitable for UI display.
     * @param {Boolean} params.refresh true to refresh data from the server - not usually needed
     * @param {String} [params.traceId]
     * @returns {Observable<Array<Share>>} emits an array of shares.
     *     May be undefined if the data has not loaded or the user logged out
     * @name get
     * @method
     */
    _shareService.get = function(params) {
        const traceIdObject = { traceId: util.withTraceId(params).traceId };
        if ((params?.refresh) || !loadedData) {
            loadedData = true;
            forkJoin({
                grants: getGrants(traceIdObject).pipe(catchError(e => of({isError: true, error: e}))),
                friends: getFriends(traceIdObject).pipe(catchError(e => of({isError: true, error: e}))),
                invites: getInvites(traceIdObject).pipe(catchError(e => of({isError: true, error: e}))),
                devices: deviceService.get(traceIdObject).pipe(util.takeFirstExistingWithNoError),
                traceId: of(traceIdObject.traceId)
            }).subscribe({
                next(result) {
                    sharesSubject.next(calcShares(result));
                }
            });
        }
        return sharesSubject.asObservable();
    };

    /**
     * @param {Object} params
     * @param {String} params.userId
     * @param {String} params.deviceId
     * @param {String} params.roleId
     * @param {String} [params.traceId]
     * @returns {Promise} A Promise that resolves after the role is set
     * @name setSingleRole
     * @method
     */
    _shareService.setSingleRole = function(params) {
        return new Observable(subscriber => {
            const internalParams = util.withTraceId(params);
            _shareService.get(internalParams).pipe(util.takeFirstExisting).subscribe({
                next: shares => {
                    // remove all grants for the user and device except for the requested one if it exists
                    const grantsToRemove = shares.filter(s => {
                        return s.isGrant
                                && internalParams.userId === s.userId
                                && internalParams.deviceId === s.deviceId
                                && internalParams.roleId !== s.roleId;
                    });
                    const removeCalls = [];
                    grantsToRemove.forEach(grant => {
                        removeCalls.push(
                            apiService.devices.removeGrant({
                                grant: grant,
                                traceId: internalParams.traceId
                            }).then(
                                () => {
                                    return Promise.resolve();
                                },
                                () => {
                                    return Promise.resolve();
                                }
                            )
                        );
                    });

                    // after remove calls are done, add a grant if necessary
                    Promise.all(removeCalls).then(
                        () => {
                            // do nothing if there is no requested role
                            if (internalParams.roleId === 'none') {
                                refreshShares(subscriber);
                                return;
                            }
                            // set the new single role
                            apiService.devices.setGrant({ grant: internalParams }).then(
                                () => refreshShares(subscriber),
                                e => subscriber.error(
                                    log({
                                        code: 'setSingleRoleSetGrant',
                                        object: internalParams,
                                        error: e,
                                        traceId: internalParams.traceId
                                    })
                                )
                            );
                        },
                        e => subscriber.error(
                            log({
                                code: 'setSingleRoleRemoveRoles',
                                object: internalParams,
                                error: e,
                                traceId: internalParams.traceId
                            })
                        )
                    );
                },
                error: e => subscriber.error(
                    log({
                        code: 'setSingleRoleNoShares',
                        object: internalParams,
                        error: e,
                        traceId: internalParams.traceId
                    })
                )
            });
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.deviceId Required
     * @param {String} params.inviteId Required
     * @param {String} [params.traceId]
     * @returns {Observable} emits when the invite has been removed
     * @name removeInvite
     * @method
     */
    _shareService.removeInvite = function(params) {
        return new Observable(subscriber => {
            if (!params?.deviceId || !params?.inviteId) {
                subscriber.error(
                    log({
                        code: 'removeInviteBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            _shareService.get({ traceId: internalParams.traceId }).pipe(util.takeFirstExisting).subscribe({
                next: () => {
                    apiService.devices.removeInvite(internalParams).then(
                        () => refreshShares(subscriber),
                        e => subscriber.error(
                            log({
                                code: 'removeInviteDoRemove',
                                object: params,
                                error: e,
                                traceId: e?.traceId
                            })
                        )
                    );
                },
                error: e => subscriber.error(
                    log({
                        code: 'removeInviteNoShares',
                        object: params,
                        error: e,
                        traceId: e?.traceId
                    })
                )
            });
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.deviceId Required
     * @param {String} params.emailAddress Required
     * @param {String} params.note
     * @param {Array<String>} params.roleIdList
     * @param {String} [params.traceId]
     * @returns {Observable<Invite>} emits a invite after successful API call
     * @name sendInvite
     * @method
     */
    _shareService.sendInvite = function(params) {
        return new Observable(subscriber => {
            if (!params?.deviceId || !params?.emailAddress) {
                subscriber.error(
                    log({
                        code: 'sendInviteBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            _shareService.get({ traceId: internalParams.traceId }).pipe(util.takeFirstExisting).subscribe({
                next: () => {
                    apiService.devices.sendUserInvitationForDevice(internalParams).then(
                        inviteResult => refreshShares(subscriber, inviteResult.data),
                        e => subscriber.error(
                            log({
                                code: 'sendInviteFail',
                                object: params,
                                error: e,
                                traceId: e?.traceId
                            })
                        )
                    );
                },
                error: e => subscriber.error(
                    log({
                        code: 'sendInviteNoShares',
                        object: params,
                        error: e,
                        traceId: e?.traceId
                    })
                )
            });
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.deviceId Required
     * @param {String} params.emailAddress Required
     * @param {Array<String>} params.roleIdList
     * @param {String} [params.traceId]
     * @returns {Observable<GuestInvite>} emits a guestInvite after successful API call
     * @name guestInvite
     * @method
     */
    _shareService.guestInvite = function(params) {
        return new Observable(subscriber => {
            if (!params?.deviceId || !params?.emailAddress) {
                subscriber.error(
                    log({
                        code: 'guestInviteBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            _shareService.get({ traceId: internalParams.traceId }).pipe(util.takeFirstExisting).subscribe({
                next: () => {
                    apiService.devices.guestInvite(internalParams).then(
                        inviteResult => refreshShares(subscriber, inviteResult.data),
                        e => subscriber.error(
                            log({
                                code: 'guestInviteFail',
                                object: params,
                                error: e,
                                traceId: e?.traceId
                            })
                        )
                    );
                },
                error: e => subscriber.error(
                    log({
                        code: 'guestInviteNoShares',
                        object: params,
                        error: e,
                        traceId: e?.traceId
                    })
                )
            });
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.token
     * @param {String} [params.traceId]
     * @returns {Observable} emits when the invite has been accepted,
     *     but no data is returned since the API returns no data.
     * @name acceptInvite
     * @method
     */
    _shareService.acceptInvite = function(params) {
        return new Observable(subscriber => {
            if (!params?.token) {
                subscriber.error(
                    log({
                        code: 'acceptInviteBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            apiService.friends.acceptInvite(internalParams).then(
                () => {
                    subscriber.next();
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'acceptInviteFailed',
                        object: params,
                        error: e,
                        traceId: e?.traceId
                    })
                )
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.friendId ID of the friend to remove
     * @param {String} [params.traceId]
     * @returns {Observable} emits when the friend has been removed,
     *     but no data is returned since the API returns no data.
     * @name unfriend
     * @method
     */
    _shareService.unfriend = function(params) {
        return new Observable(subscriber => {
            if (!params?.friendId) {
                subscriber.error(
                    log({
                        code: 'unfriendBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            apiService.friends.remove(internalParams).then(
                () => refreshShares(subscriber),
                e => subscriber.error(
                    log({
                        code: 'unfriendFailed',
                        object: params,
                        error: e,
                        traceId: e?.traceId
                    })
                )
            );
        });
    };

    // Any time loggedIn emits false, run clear
    apiService.loggedIn().pipe(filter(l => !l)).subscribe(() => clear());

    return Object.freeze(_shareService);
}

export default createShareService;
